/**
 * Identity-session heartbeat (ADR-0022 follow-up; the "I have to log in too often" fix).
 *
 * The identity session at worker-auth idles out after 30 minutes without a `/auth/*`
 * request, but the SPA's steady-state traffic is all `/api/v1/*` (worker-api), which only
 * slides the FIRM session. So an examiner working for an hour would find the identity dead
 * on the next reload. This keeps it alive with the cheapest possible touch — the store's
 * `refresh()` (worker-auth `GET /auth/me`, deduped with any in-flight refresh) — on every
 * return to the tab and on a timer while the tab is visible. It never fires while the tab
 * is hidden, never while signed out, and never more often than MIN_GAP_MS.
 *
 * A FAILED touch — the session died while the tab sat open past its cap — makes `refresh()`
 * flip the store (`unauthenticated`, or firm-less). Nothing HERE navigates on that: it is the
 * session guard (router/session-guard.ts, installed in main.tsx) that then moves the user to
 * /login, or silently re-selects the firm / lands on the picker. Route guards run only on
 * navigation, and this heartbeat is the one thing that notices a dead session with nobody
 * navigating — which is why a failed refresh now ends on /login instead of a stuck shell.
 */
import { useAuth } from '../store/auth-store.ts';

export const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;
export const MIN_GAP_MS = 60 * 1000;

export interface HeartbeatOptions {
  intervalMs?: number | undefined;
  minGapMs?: number | undefined;
  /** Injected for tests; defaults to the store's refresh. */
  touch?: (() => Promise<void>) | undefined;
  /** Injected for tests; defaults to `document`. */
  doc?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | undefined;
  now?: (() => number) | undefined;
}

/** Start the heartbeat; returns a stop function. */
export function startSessionHeartbeat(options: HeartbeatOptions = {}): () => void {
  const intervalMs = options.intervalMs ?? HEARTBEAT_INTERVAL_MS;
  const minGapMs = options.minGapMs ?? MIN_GAP_MS;
  const touch = options.touch ?? (() => useAuth.getState().refresh());
  const doc = options.doc ?? document;
  const now = options.now ?? (() => Date.now());

  let lastTouch = now();

  function beat(): void {
    if (doc.visibilityState !== 'visible') return;
    if (useAuth.getState().status !== 'authenticated') return;
    if (now() - lastTouch < minGapMs) return;
    lastTouch = now();
    void touch().catch(() => {
      /* refresh() already reconciles the store on failure */
    });
  }

  const onVisibility = (): void => beat();
  doc.addEventListener('visibilitychange', onVisibility);
  const timer = setInterval(beat, intervalMs);

  return () => {
    doc.removeEventListener('visibilitychange', onVisibility);
    clearInterval(timer);
  };
}
